import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const SUPPORTED_LANGUAGES = {
  en: { name: 'English', flag: '🇬🇧' },
  fr: { name: 'Français', flag: '🇫🇷' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  es: { name: 'Español', flag: '🇪🇸' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  ja: { name: '日本語', flag: '🇯🇵' }
};

// Static UI translations for common elements
const UI_TRANSLATIONS = {
  en: {
    // Navigation
    home: 'Home',
    shop: 'Shop',
    search: 'Search',
    signIn: 'Sign In',
    signOut: 'Sign Out',
    myAccount: 'My Account',
    admin: 'Admin',
    adminPanel: 'Admin Panel',
    logout: 'Logout',
    
    // Cart & Checkout
    cart: 'Cart',
    addToCart: 'Add to Cart',
    checkout: 'Checkout',
    viewCart: 'View Cart',
    continueShopping: 'Continue Shopping',
    emptyCart: 'Your cart is empty',
    clearCart: 'Clear Cart',
    removeItem: 'Remove',
    updateQuantity: 'Update Quantity',
    yourCart: 'Your Cart',
    secureCheckout: 'Secure Checkout',
    placeOrder: 'Place Order',
    processing: 'Processing...',
    
    // Products
    products: 'Products',
    categories: 'Categories',
    price: 'Price',
    quantity: 'Quantity',
    total: 'Total',
    subtotal: 'Subtotal',
    shipping: 'Shipping',
    vat: 'VAT',
    description: 'Description',
    specifications: 'Specifications',
    reviews: 'Reviews',
    sortBy: 'Sort by',
    searchProducts: 'Search products',
    awaitingImage: 'Awaiting Image',
    default: 'Default',
    priceHighToLow: 'Price: High to Low',
    priceLowToHigh: 'Price: Low to High',
    nameAZ: 'Name: A-Z',
    nameZA: 'Name: Z-A',
    newestFirst: 'Newest First',
    filters: 'Filters',
    noImage: 'No Image',
    
    // Shop Categories
    shopByModel: 'Shop by Model',
    paintProtection: 'Paint Protection',
    vinyl: 'Vinyl',
    vinylTools: 'Vinyl & Tools',
    miniRelated: 'Mini Related',
    
    // Wishlist
    wishlist: 'Wishlist',
    yourWishlist: 'Your Wishlist',
    emptyWishlist: 'Your Wishlist is Empty',
    saveItemsHint: 'Save items you love by clicking the heart icon on products.',
    clearAll: 'Clear All',
    removedFromWishlist: 'removed from wishlist',
    addedToWishlist: 'added to wishlist',
    
    // Account
    account: 'Account',
    my: 'My',
    orderHistory: 'Order History',
    noOrdersYet: 'No orders yet',
    startShopping: 'Start Shopping',
    homePage: 'Home Page',
    clickPhotoToChange: 'Click photo to change',
    profileImageUpdated: 'Profile image updated!',
    profileImageRemoved: 'Profile image removed',
    imageTooLarge: 'Image too large. Maximum size is 5MB.',
    removeProfileImage: 'Remove profile image',
    
    // Auth
    login: 'Login',
    register: 'Register',
    createAccount: 'Create Account',
    welcomeBack: 'Welcome Back',
    getStarted: 'Get Started',
    fullName: 'Full Name',
    password: 'Password',
    confirmPassword: 'Confirm Password',
    loggingIn: 'Logging in...',
    creatingAccount: 'Creating account...',
    dontHaveAccount: "Don't have an account?",
    createOne: 'Create one',
    alreadyHaveAccount: 'Already have an account?',
    passwordsDontMatch: "Passwords don't match",
    passwordMinLength: 'Password must be at least 6 characters',
    accountCreatedSuccess: 'Account created successfully!',
    
    // Contact
    contactUs: 'Contact Us',
    getInTouch: 'Get In Touch',
    sendUsMessage: 'Send Us a Message',
    name: 'Name',
    subject: 'Subject',
    message: 'Message',
    sendMessage: 'Send Message',
    sending: 'Sending...',
    emailUs: 'Email Us',
    whatsApp: 'WhatsApp',
    textMessagesOnly: 'Text messages only',
    businessHours: 'Business Hours',
    followUs: 'Follow Us',
    commonQuestions: 'Common Questions',
    weTypicallyRespond: 'We typically respond within 24-48 hours',
    
    // Forms
    email: 'Email',
    phone: 'Phone',
    firstName: 'First Name',
    lastName: 'Last Name',
    address: 'Address',
    city: 'City',
    postcode: 'Postcode',
    country: 'Country',
    optional: 'optional',
    required: 'required',
    
    // Discount & Shipping
    discountCode: 'Discount Code',
    enterCouponCode: 'Enter coupon code',
    apply: 'Apply',
    estimateShipping: 'Estimate Shipping',
    estimate: 'Estimate',
    orderSummary: 'Order Summary',
    addInstructionsToOrder: 'Add further Instruction to order',
    specialInstructionsPlaceholder: 'Enter any special instructions for your order (e.g., custom text, specific requirements)...',
    termsAcceptRequired: 'Please accept the terms and conditions to proceed',
    iAgreeTo: 'I agree to the',
    termsOfService: 'Terms of Service',
    and: 'and',
    shippingReturnsPolicy: 'Shipping & Returns Policy',
    sslSecure: 'SSL Secure',
    sslSecureCheckout: 'SSL Secure Checkout',
    paymentsSecuredByStripe: 'Payments securely processed by Stripe',
    vatExemptNotice: 'VAT exempt for international orders. You may be liable for local import duties/taxes upon delivery.',
    
    // Shipping & Returns Page
    shippingReturns: 'Shipping and Returns',
    deliveryInformation: 'Delivery Information',
    importantMadeToOrder: 'Important: Made to Order',
    madeToOrderNotice: 'All of our graphics and badges are made to order! This means that there may be unforeseen delays in sending orders.',
    currentDispatchTime: 'Our current expected dispatch time is 5 working days from time of order placement.',
    subjectToChange: 'This is subject to change without notice.',
    pleaseBareinMind: 'Please bare this in mind before ordering.',
    shippingCalculation: 'Shipping Calculation',
    upsExtendedAreas: 'UPS Extended Delivery Areas',
    returnsPolicy: 'Returns Policy',
    returnsAddress: 'Returns Address',
    questionsTitle: 'Questions?',
    usa: 'USA',
    uk: 'UK',
    europe: 'Europe',
    worldwide: 'Worldwide',
    backToCart: 'Back to cart',
    
    // How to Apply Page
    howToApply: 'How to Apply',
    watchTutorials: 'Watch our step-by-step video tutorials to learn how to perfectly apply your Mini Stripes stickers and decals',
    comingSoon: 'Coming Soon!',
    workingOnTutorials: "We're working on creating helpful video tutorials for you. Check back soon!",
    allTutorials: 'All Tutorials',
    applicationTips: 'Application Tips',
    cleanSurface: 'Clean Surface',
    cleanSurfaceTip: 'Ensure the surface is clean, dry, and free from dust, wax, or oils before application.',
    temperature: 'Temperature',
    temperatureTip: 'Apply in temperatures between 15-25°C (60-77°F) for best adhesion results.',
    takeYourTime: 'Take Your Time',
    takeYourTimeTip: 'Work slowly from one edge to avoid bubbles. Use a squeegee for smooth results.',
    
    // Support pages
    customsDuty: 'Customs / Duty Charges',
    usaDelivery: 'USA and Delivery Duties',
    termsConditions: 'Terms and Conditions',
    
    // Footer
    myOrders: 'My Orders',
    addresses: 'Addresses',
    support: 'Support',
    allRightsReserved: 'All rights reserved.',
    paymentSecuredByStripe: 'Payment secured by Stripe',
    
    // Common
    loading: 'Loading...',
    noResults: 'No results found',
    backToHome: 'Back to Home Page',
    backToProducts: 'Back to Products',
    share: 'Share',
    colour: 'Colour',
    instructions: 'Instructions',
    order: 'Order',
    status: 'Status',
    date: 'Date',
    comingSoonLabel: 'Coming soon',
    creditDebitCard: 'Credit / Debit Card',
    poweredByStripe: 'Powered by Stripe',
    payPal: 'PayPal',
    paymentMethod: 'Payment Method',
    shippingAddress: 'Shipping Address',
    contact: 'Contact',
    
    // Notifications
    notifications: 'Notifications',
    noNotifications: 'No notifications',
    
    // Sell tab
    sell: 'Sell',
    sellOnForum: 'Sell on Forum',
    miniStripesOwnersForum: 'Ministripes Owners Forum',
    
    // Forum / Owners Club
    backToAccount: 'Back to Account',
    backToShop: 'Back to Shop',
    shareYourMini: 'Share your Mini, discuss graphics, sell and connect with fellow Mini enthusiasts',
    myProfile: 'My Profile',
    forum: 'Forum',
    gallery: 'Gallery',
    forSale: 'For Sale',
    newTopic: 'New Topic',
    newListing: 'New Listing',
    searchTopics: 'Search topics...',
    filterByTopic: 'Filter by Topic',
    allTopics: 'All Topics',
    noTopicsYet: 'No topics yet. Start the conversation!',
    createFirstTopic: 'Create First Topic',
    photoGallery: 'Photo Gallery',
    noPhotosYet: 'No photos yet. Share your Mini in the Forum!',
    goToForum: 'Go to Forum',
    miniRelatedForSale: 'Mini Related for Sale',
    welcomeToMarketplace: 'Welcome to the Mini Marketplace!',
    buyAndSellMini: 'Buy and sell Mini cars, parts, accessories, and more. Create a listing by clicking "New Listing" and include photos and set your price.',
    noListingsYet: 'No listings yet. Be the first to sell!',
    createFirstListing: 'Create First Listing',
    memberSince: 'Member since',
    yourTopics: 'Your Topics',
    noTopicsCreated: "You haven't created any topics yet",
    likes: 'likes',
    replies: 'replies',
    views: 'views',
    writeComment: 'Write a comment...',
    postComment: 'Post',
    rateSeller: 'Rate Seller',
    sendMessage: 'Send Message',
    markAsSold: 'Mark as Sold',
    markAvailable: 'Mark Available',
    sold: 'SOLD',
    deleteTopic: 'Delete Topic',
    editTopic: 'Edit',
    location: 'Location',
    selectCategory: 'Select a category',
    
    // Forum Categories
    bonnetStripes: 'Bonnet Stripes',
    generalDiscussion: 'General Discussion',
    roofGraphics: 'Roof Graphics',
    showcaseYourMini: 'Showcase Your Mini',
    sideStripes: 'Side Stripes',
    tipsAndTricks: 'Tips & Tricks',
    
    // Create Listing/Topic Modal
    createNewListing: 'Create New Listing',
    createNewTopic: 'Create New Topic',
    postingAs: 'Posting as',
    sellingTips: 'Selling Tips:',
    sellingTipsText: 'Include clear photos and item condition. Set your price and location below.',
    category: 'Category',
    newTopicCategory: 'New Topic Category',
    enterNewCategoryName: 'Enter new category name',
    add: 'Add',
    cancelUseExisting: 'Cancel - use existing category',
    title: 'Title',
    titlePlaceholder: 'What\'s your topic about?',
    content: 'Content',
    contentPlaceholder: 'Share your thoughts, tips, or questions...',
    priceLabel: 'Price (£)',
    pricePlaceholder: 'Enter price (e.g. 99.99)',
    priceHelp: 'Leave empty for "Price on request" or "Free"',
    locationLabel: 'Location',
    locationPlaceholder: 'e.g. Birmingham, London, Manchester',
    locationHelp: 'Help buyers know where the item is located',
    photosOptional: 'Photos (optional)',
    uploading: 'Uploading...',
    uploadPhoto: 'Upload Photo',
    postTopic: 'Post Topic',
    postListing: 'Post Listing',
    priceOnRequest: 'Price on request',
    
    // My Profile Section
    topics: 'Topics',
    createYourFirstTopic: 'Create Your First Topic',
    noTopicsCreatedYet: 'You haven\'t created any topics yet',
    editProfile: 'Edit Profile'
  },
  fr: {
    // Navigation
    home: 'Accueil',
    shop: 'Boutique',
    search: 'Rechercher',
    signIn: 'Connexion',
    signOut: 'Déconnexion',
    myAccount: 'Mon Compte',
    admin: 'Admin',
    adminPanel: 'Panneau Admin',
    logout: 'Déconnexion',
    
    // Cart & Checkout
    cart: 'Panier',
    addToCart: 'Ajouter au Panier',
    checkout: 'Commander',
    viewCart: 'Voir le Panier',
    continueShopping: 'Continuer les Achats',
    emptyCart: 'Votre panier est vide',
    clearCart: 'Vider le Panier',
    removeItem: 'Supprimer',
    updateQuantity: 'Modifier la Quantité',
    yourCart: 'Votre Panier',
    secureCheckout: 'Paiement Sécurisé',
    placeOrder: 'Passer la Commande',
    processing: 'Traitement...',
    
    // Products
    products: 'Produits',
    categories: 'Catégories',
    price: 'Prix',
    quantity: 'Quantité',
    total: 'Total',
    subtotal: 'Sous-total',
    shipping: 'Livraison',
    vat: 'TVA',
    description: 'Description',
    specifications: 'Spécifications',
    reviews: 'Avis',
    sortBy: 'Trier par',
    searchProducts: 'Rechercher des produits',
    awaitingImage: 'Image en attente',
    default: 'Défaut',
    priceHighToLow: 'Prix: Décroissant',
    priceLowToHigh: 'Prix: Croissant',
    nameAZ: 'Nom: A-Z',
    nameZA: 'Nom: Z-A',
    newestFirst: 'Plus Récent',
    filters: 'Filtres',
    noImage: 'Pas d\'image',
    
    // Shop Categories
    shopByModel: 'Acheter par Modèle',
    paintProtection: 'Protection Peinture',
    vinyl: 'Vinyle',
    vinylTools: 'Vinyle & Outils',
    miniRelated: 'Mini Associé',
    
    // Wishlist
    wishlist: 'Liste de souhaits',
    yourWishlist: 'Votre Liste de Souhaits',
    emptyWishlist: 'Votre Liste de Souhaits est Vide',
    saveItemsHint: 'Enregistrez les articles que vous aimez en cliquant sur l\'icône cœur.',
    clearAll: 'Tout Effacer',
    removedFromWishlist: 'retiré de la liste de souhaits',
    addedToWishlist: 'ajouté à la liste de souhaits',
    
    // Account
    account: 'Compte',
    my: 'Mon',
    orderHistory: 'Historique des Commandes',
    noOrdersYet: 'Aucune commande',
    startShopping: 'Commencer vos Achats',
    homePage: 'Page d\'Accueil',
    clickPhotoToChange: 'Cliquez sur la photo pour changer',
    profileImageUpdated: 'Image de profil mise à jour!',
    profileImageRemoved: 'Image de profil supprimée',
    imageTooLarge: 'Image trop grande. Taille maximale: 5Mo.',
    removeProfileImage: 'Supprimer l\'image de profil',
    
    // Auth
    login: 'Connexion',
    register: 'S\'inscrire',
    createAccount: 'Créer un Compte',
    welcomeBack: 'Bon Retour',
    getStarted: 'Commencer',
    fullName: 'Nom Complet',
    password: 'Mot de passe',
    confirmPassword: 'Confirmer le Mot de passe',
    loggingIn: 'Connexion...',
    creatingAccount: 'Création du compte...',
    dontHaveAccount: 'Pas de compte?',
    createOne: 'Créer un',
    alreadyHaveAccount: 'Déjà un compte?',
    passwordsDontMatch: 'Les mots de passe ne correspondent pas',
    passwordMinLength: 'Le mot de passe doit comporter au moins 6 caractères',
    accountCreatedSuccess: 'Compte créé avec succès!',
    
    // Contact
    contactUs: 'Nous Contacter',
    getInTouch: 'Contactez-Nous',
    sendUsMessage: 'Envoyez-nous un Message',
    name: 'Nom',
    subject: 'Sujet',
    message: 'Message',
    sendMessage: 'Envoyer le Message',
    sending: 'Envoi...',
    emailUs: 'Envoyez-nous un Email',
    whatsApp: 'WhatsApp',
    textMessagesOnly: 'Messages texte uniquement',
    businessHours: 'Heures d\'Ouverture',
    followUs: 'Suivez-Nous',
    commonQuestions: 'Questions Fréquentes',
    weTypicallyRespond: 'Nous répondons généralement sous 24-48 heures',
    
    // Forms
    email: 'Email',
    phone: 'Téléphone',
    firstName: 'Prénom',
    lastName: 'Nom',
    address: 'Adresse',
    city: 'Ville',
    postcode: 'Code Postal',
    country: 'Pays',
    optional: 'optionnel',
    required: 'obligatoire',
    
    // Discount & Shipping
    discountCode: 'Code de Réduction',
    enterCouponCode: 'Entrez le code promo',
    apply: 'Appliquer',
    estimateShipping: 'Estimer la Livraison',
    estimate: 'Estimer',
    orderSummary: 'Récapitulatif',
    addInstructionsToOrder: 'Ajouter des instructions à la commande',
    specialInstructionsPlaceholder: 'Entrez des instructions spéciales pour votre commande...',
    termsAcceptRequired: 'Veuillez accepter les conditions générales pour continuer',
    iAgreeTo: 'J\'accepte les',
    termsOfService: 'Conditions d\'Utilisation',
    and: 'et',
    shippingReturnsPolicy: 'Politique de Livraison et Retours',
    sslSecure: 'SSL Sécurisé',
    sslSecureCheckout: 'Paiement SSL Sécurisé',
    paymentsSecuredByStripe: 'Paiements sécurisés par Stripe',
    vatExemptNotice: 'Exonéré de TVA pour les commandes internationales. Vous pourriez être redevable de droits d\'importation.',
    
    // Shipping & Returns Page
    shippingReturns: 'Livraison et Retours',
    deliveryInformation: 'Informations de Livraison',
    importantMadeToOrder: 'Important: Fait sur Commande',
    madeToOrderNotice: 'Tous nos graphiques sont fabriqués sur commande! Cela peut entraîner des retards.',
    currentDispatchTime: 'Notre délai d\'expédition actuel est de 5 jours ouvrables.',
    subjectToChange: 'Sujet à modification sans préavis.',
    pleaseBareinMind: 'Veuillez en tenir compte avant de commander.',
    shippingCalculation: 'Calcul des Frais de Port',
    upsExtendedAreas: 'Zones de Livraison Étendues UPS',
    returnsPolicy: 'Politique de Retours',
    returnsAddress: 'Adresse de Retour',
    questionsTitle: 'Questions?',
    usa: 'États-Unis',
    uk: 'Royaume-Uni',
    europe: 'Europe',
    worldwide: 'Monde Entier',
    backToCart: 'Retour au panier',
    
    // How to Apply Page
    howToApply: 'Comment Appliquer',
    watchTutorials: 'Regardez nos tutoriels vidéo pour apprendre à appliquer parfaitement vos autocollants',
    comingSoon: 'Bientôt Disponible!',
    workingOnTutorials: 'Nous préparons des tutoriels vidéo utiles. Revenez bientôt!',
    allTutorials: 'Tous les Tutoriels',
    applicationTips: 'Conseils d\'Application',
    cleanSurface: 'Surface Propre',
    cleanSurfaceTip: 'Assurez-vous que la surface est propre, sèche et exempte de poussière.',
    temperature: 'Température',
    temperatureTip: 'Appliquez entre 15-25°C pour de meilleurs résultats.',
    takeYourTime: 'Prenez Votre Temps',
    takeYourTimeTip: 'Travaillez lentement pour éviter les bulles. Utilisez une raclette.',
    
    // Support pages
    customsDuty: 'Douanes / Frais',
    usaDelivery: 'USA et Frais de Livraison',
    termsConditions: 'Conditions Générales',
    
    // Footer
    myOrders: 'Mes Commandes',
    addresses: 'Adresses',
    support: 'Assistance',
    allRightsReserved: 'Tous droits réservés.',
    paymentSecuredByStripe: 'Paiement sécurisé par Stripe',
    
    // Common
    loading: 'Chargement...',
    noResults: 'Aucun résultat trouvé',
    backToHome: 'Retour à l\'Accueil',
    backToProducts: 'Retour aux Produits',
    share: 'Partager',
    colour: 'Couleur',
    instructions: 'Instructions',
    order: 'Commande',
    status: 'Statut',
    date: 'Date',
    comingSoonLabel: 'Bientôt',
    creditDebitCard: 'Carte de Crédit / Débit',
    poweredByStripe: 'Propulsé par Stripe',
    payPal: 'PayPal',
    paymentMethod: 'Méthode de Paiement',
    shippingAddress: 'Adresse de Livraison',
    contact: 'Contact',
    
    // Notifications
    notifications: 'Notifications',
    noNotifications: 'Aucune notification',
    
    // Sell tab
    sell: 'Vendre',
    sellOnForum: 'Vendre sur le Forum',
    miniStripesOwnersForum: 'Forum Mini Stripes Owners',
    
    // Forum / Owners Club
    backToAccount: 'Retour au Compte',
    backToShop: 'Retour à la Boutique',
    shareYourMini: 'Partagez votre Mini, discutez des graphiques, vendez et connectez-vous avec d\'autres passionnés de Mini',
    myProfile: 'Mon Profil',
    forum: 'Forum',
    gallery: 'Galerie',
    forSale: 'À Vendre',
    newTopic: 'Nouveau Sujet',
    newListing: 'Nouvelle Annonce',
    searchTopics: 'Rechercher des sujets...',
    filterByTopic: 'Filtrer par Sujet',
    allTopics: 'Tous les Sujets',
    noTopicsYet: 'Pas encore de sujets. Lancez la conversation!',
    createFirstTopic: 'Créer le Premier Sujet',
    photoGallery: 'Galerie Photos',
    noPhotosYet: 'Pas encore de photos. Partagez votre Mini sur le Forum!',
    goToForum: 'Aller au Forum',
    miniRelatedForSale: 'Mini à Vendre',
    welcomeToMarketplace: 'Bienvenue sur le Marché Mini!',
    buyAndSellMini: 'Achetez et vendez des Mini, pièces, accessoires et plus. Créez une annonce en cliquant sur "Nouvelle Annonce" et ajoutez des photos et votre prix.',
    noListingsYet: 'Pas encore d\'annonces. Soyez le premier à vendre!',
    createFirstListing: 'Créer la Première Annonce',
    memberSince: 'Membre depuis',
    yourTopics: 'Vos Sujets',
    noTopicsCreated: 'Vous n\'avez pas encore créé de sujet',
    likes: 'j\'aime',
    replies: 'réponses',
    views: 'vues',
    writeComment: 'Écrire un commentaire...',
    postComment: 'Publier',
    rateSeller: 'Noter le Vendeur',
    sendMessage: 'Envoyer un Message',
    markAsSold: 'Marquer comme Vendu',
    markAvailable: 'Marquer Disponible',
    sold: 'VENDU',
    deleteTopic: 'Supprimer le Sujet',
    editTopic: 'Modifier',
    location: 'Localisation',
    selectCategory: 'Sélectionner une catégorie',
    
    // Forum Categories
    bonnetStripes: 'Bandes de Capot',
    generalDiscussion: 'Discussion Générale',
    roofGraphics: 'Graphiques de Toit',
    showcaseYourMini: 'Montrez Votre Mini',
    sideStripes: 'Bandes Latérales',
    tipsAndTricks: 'Trucs et Astuces',
    
    // Create Listing/Topic Modal
    createNewListing: 'Créer une Nouvelle Annonce',
    createNewTopic: 'Créer un Nouveau Sujet',
    postingAs: 'Publié en tant que',
    sellingTips: 'Conseils de vente:',
    sellingTipsText: 'Incluez des photos claires et l\'état de l\'article. Définissez votre prix et votre localisation ci-dessous.',
    category: 'Catégorie',
    newTopicCategory: 'Nouvelle Catégorie',
    enterNewCategoryName: 'Entrez le nom de la nouvelle catégorie',
    add: 'Ajouter',
    cancelUseExisting: 'Annuler - utiliser une catégorie existante',
    title: 'Titre',
    titlePlaceholder: 'De quoi parle votre sujet?',
    content: 'Contenu',
    contentPlaceholder: 'Partagez vos pensées, conseils ou questions...',
    priceLabel: 'Prix (£)',
    pricePlaceholder: 'Entrez le prix (ex. 99.99)',
    priceHelp: 'Laissez vide pour "Prix sur demande" ou "Gratuit"',
    locationLabel: 'Localisation',
    locationPlaceholder: 'ex. Birmingham, Londres, Manchester',
    locationHelp: 'Aidez les acheteurs à savoir où se trouve l\'article',
    photosOptional: 'Photos (optionnel)',
    uploading: 'Téléchargement...',
    uploadPhoto: 'Télécharger une Photo',
    postTopic: 'Publier le Sujet',
    postListing: 'Publier l\'Annonce',
    priceOnRequest: 'Prix sur demande',
    
    // My Profile Section
    topics: 'Sujets',
    createYourFirstTopic: 'Créer Votre Premier Sujet',
    noTopicsCreatedYet: 'Vous n\'avez pas encore créé de sujet',
    editProfile: 'Modifier le Profil'
  },
  de: {
    // Navigation
    home: 'Startseite',
    shop: 'Shop',
    search: 'Suchen',
    signIn: 'Anmelden',
    signOut: 'Abmelden',
    myAccount: 'Mein Konto',
    admin: 'Admin',
    adminPanel: 'Admin-Bereich',
    logout: 'Abmelden',
    
    // Cart & Checkout
    cart: 'Warenkorb',
    addToCart: 'In den Warenkorb',
    checkout: 'Zur Kasse',
    viewCart: 'Warenkorb anzeigen',
    continueShopping: 'Weiter einkaufen',
    emptyCart: 'Ihr Warenkorb ist leer',
    clearCart: 'Warenkorb leeren',
    removeItem: 'Entfernen',
    updateQuantity: 'Menge ändern',
    yourCart: 'Ihr Warenkorb',
    secureCheckout: 'Sichere Zahlung',
    placeOrder: 'Bestellung aufgeben',
    processing: 'Verarbeitung...',
    
    // Products
    products: 'Produkte',
    categories: 'Kategorien',
    price: 'Preis',
    quantity: 'Menge',
    total: 'Gesamt',
    subtotal: 'Zwischensumme',
    shipping: 'Versand',
    vat: 'MwSt',
    description: 'Beschreibung',
    specifications: 'Spezifikationen',
    reviews: 'Bewertungen',
    sortBy: 'Sortieren nach',
    searchProducts: 'Produkte suchen',
    awaitingImage: 'Bild ausstehend',
    default: 'Standard',
    priceHighToLow: 'Preis: Hoch zu Niedrig',
    priceLowToHigh: 'Preis: Niedrig zu Hoch',
    nameAZ: 'Name: A-Z',
    nameZA: 'Name: Z-A',
    newestFirst: 'Neueste zuerst',
    filters: 'Filter',
    noImage: 'Kein Bild',
    
    // Shop Categories
    shopByModel: 'Nach Modell einkaufen',
    paintProtection: 'Lackschutz',
    vinyl: 'Vinyl',
    vinylTools: 'Vinyl & Werkzeuge',
    miniRelated: 'Mini Zubehör',
    
    // Wishlist
    wishlist: 'Wunschliste',
    yourWishlist: 'Ihre Wunschliste',
    emptyWishlist: 'Ihre Wunschliste ist leer',
    saveItemsHint: 'Speichern Sie Artikel durch Klicken auf das Herz-Symbol.',
    clearAll: 'Alles löschen',
    removedFromWishlist: 'von Wunschliste entfernt',
    addedToWishlist: 'zur Wunschliste hinzugefügt',
    
    // Account
    account: 'Konto',
    my: 'Mein',
    orderHistory: 'Bestellverlauf',
    noOrdersYet: 'Noch keine Bestellungen',
    startShopping: 'Jetzt einkaufen',
    homePage: 'Startseite',
    clickPhotoToChange: 'Klicken Sie auf das Foto zum Ändern',
    profileImageUpdated: 'Profilbild aktualisiert!',
    profileImageRemoved: 'Profilbild entfernt',
    imageTooLarge: 'Bild zu groß. Maximale Größe: 5MB.',
    removeProfileImage: 'Profilbild entfernen',
    
    // Auth
    login: 'Anmelden',
    register: 'Registrieren',
    createAccount: 'Konto erstellen',
    welcomeBack: 'Willkommen zurück',
    getStarted: 'Loslegen',
    fullName: 'Vollständiger Name',
    password: 'Passwort',
    confirmPassword: 'Passwort bestätigen',
    loggingIn: 'Anmeldung...',
    creatingAccount: 'Konto wird erstellt...',
    dontHaveAccount: 'Noch kein Konto?',
    createOne: 'Erstellen Sie eines',
    alreadyHaveAccount: 'Bereits ein Konto?',
    passwordsDontMatch: 'Passwörter stimmen nicht überein',
    passwordMinLength: 'Passwort muss mindestens 6 Zeichen haben',
    accountCreatedSuccess: 'Konto erfolgreich erstellt!',
    
    // Contact
    contactUs: 'Kontakt',
    getInTouch: 'Kontaktieren Sie uns',
    sendUsMessage: 'Senden Sie uns eine Nachricht',
    name: 'Name',
    subject: 'Betreff',
    message: 'Nachricht',
    sendMessage: 'Nachricht senden',
    sending: 'Wird gesendet...',
    emailUs: 'Email senden',
    whatsApp: 'WhatsApp',
    textMessagesOnly: 'Nur Textnachrichten',
    businessHours: 'Geschäftszeiten',
    followUs: 'Folgen Sie uns',
    commonQuestions: 'Häufige Fragen',
    weTypicallyRespond: 'Wir antworten in der Regel innerhalb von 24-48 Stunden',
    
    // Forms
    email: 'E-Mail',
    phone: 'Telefon',
    firstName: 'Vorname',
    lastName: 'Nachname',
    address: 'Adresse',
    city: 'Stadt',
    postcode: 'Postleitzahl',
    country: 'Land',
    optional: 'optional',
    required: 'erforderlich',
    
    // Discount & Shipping
    discountCode: 'Rabattcode',
    enterCouponCode: 'Gutscheincode eingeben',
    apply: 'Anwenden',
    estimateShipping: 'Versand berechnen',
    estimate: 'Berechnen',
    orderSummary: 'Bestellübersicht',
    addInstructionsToOrder: 'Anweisungen zur Bestellung hinzufügen',
    specialInstructionsPlaceholder: 'Geben Sie spezielle Anweisungen für Ihre Bestellung ein...',
    termsAcceptRequired: 'Bitte akzeptieren Sie die AGB um fortzufahren',
    iAgreeTo: 'Ich stimme zu',
    termsOfService: 'Nutzungsbedingungen',
    and: 'und',
    shippingReturnsPolicy: 'Versand- und Rückgaberichtlinien',
    sslSecure: 'SSL gesichert',
    sslSecureCheckout: 'SSL-gesicherte Zahlung',
    paymentsSecuredByStripe: 'Zahlungen gesichert durch Stripe',
    vatExemptNotice: 'MwSt-befreit für internationale Bestellungen. Es können Einfuhrzölle anfallen.',
    
    // Shipping & Returns Page
    shippingReturns: 'Versand und Rückgabe',
    deliveryInformation: 'Lieferinformationen',
    importantMadeToOrder: 'Wichtig: Auf Bestellung gefertigt',
    madeToOrderNotice: 'Alle unsere Grafiken werden auf Bestellung gefertigt! Dies kann zu Verzögerungen führen.',
    currentDispatchTime: 'Unsere aktuelle Versandzeit beträgt 5 Werktage.',
    subjectToChange: 'Änderungen vorbehalten.',
    pleaseBareinMind: 'Bitte beachten Sie dies vor der Bestellung.',
    shippingCalculation: 'Versandkostenberechnung',
    upsExtendedAreas: 'UPS Erweiterte Liefergebiete',
    returnsPolicy: 'Rückgaberichtlinien',
    returnsAddress: 'Rücksendeadresse',
    questionsTitle: 'Fragen?',
    usa: 'USA',
    uk: 'Großbritannien',
    europe: 'Europa',
    worldwide: 'Weltweit',
    backToCart: 'Zurück zum Warenkorb',
    
    // How to Apply Page
    howToApply: 'Anwendung',
    watchTutorials: 'Sehen Sie unsere Schritt-für-Schritt Video-Tutorials',
    comingSoon: 'Demnächst verfügbar!',
    workingOnTutorials: 'Wir arbeiten an hilfreichen Video-Tutorials. Schauen Sie bald wieder vorbei!',
    allTutorials: 'Alle Tutorials',
    applicationTips: 'Anwendungstipps',
    cleanSurface: 'Saubere Oberfläche',
    cleanSurfaceTip: 'Stellen Sie sicher, dass die Oberfläche sauber, trocken und frei von Staub ist.',
    temperature: 'Temperatur',
    temperatureTip: 'Anwendung bei 15-25°C für beste Ergebnisse.',
    takeYourTime: 'Nehmen Sie sich Zeit',
    takeYourTimeTip: 'Arbeiten Sie langsam um Blasen zu vermeiden. Verwenden Sie einen Rakel.',
    
    // Support pages
    customsDuty: 'Zoll / Gebühren',
    usaDelivery: 'USA und Liefergebühren',
    termsConditions: 'AGB',
    
    // Footer
    myOrders: 'Meine Bestellungen',
    addresses: 'Adressen',
    support: 'Support',
    allRightsReserved: 'Alle Rechte vorbehalten.',
    paymentSecuredByStripe: 'Zahlung gesichert durch Stripe',
    
    // Common
    loading: 'Wird geladen...',
    noResults: 'Keine Ergebnisse gefunden',
    backToHome: 'Zurück zur Startseite',
    backToProducts: 'Zurück zu Produkten',
    share: 'Teilen',
    colour: 'Farbe',
    instructions: 'Anweisungen',
    order: 'Bestellung',
    status: 'Status',
    date: 'Datum',
    comingSoonLabel: 'Demnächst',
    creditDebitCard: 'Kredit- / Debitkarte',
    poweredByStripe: 'Powered by Stripe',
    payPal: 'PayPal',
    paymentMethod: 'Zahlungsmethode',
    shippingAddress: 'Lieferadresse',
    contact: 'Kontakt',
    
    // Notifications
    notifications: 'Benachrichtigungen',
    noNotifications: 'Keine Benachrichtigungen',
    
    // Sell tab
    sell: 'Verkaufen',
    sellOnForum: 'Im Forum verkaufen',
    miniStripesOwnersForum: 'Ministripes Owners Forum',
    
    // Forum / Owners Club
    backToAccount: 'Zurück zum Konto',
    backToShop: 'Zurück zum Shop',
    shareYourMini: 'Teilen Sie Ihren Mini, diskutieren Sie Grafiken, verkaufen und vernetzen Sie sich mit anderen Mini-Enthusiasten',
    myProfile: 'Mein Profil',
    forum: 'Forum',
    gallery: 'Galerie',
    forSale: 'Zu Verkaufen',
    newTopic: 'Neues Thema',
    newListing: 'Neue Anzeige',
    searchTopics: 'Themen suchen...',
    filterByTopic: 'Nach Thema filtern',
    allTopics: 'Alle Themen',
    noTopicsYet: 'Noch keine Themen. Starten Sie das Gespräch!',
    createFirstTopic: 'Erstes Thema erstellen',
    photoGallery: 'Fotogalerie',
    noPhotosYet: 'Noch keine Fotos. Teilen Sie Ihren Mini im Forum!',
    goToForum: 'Zum Forum',
    miniRelatedForSale: 'Mini Zubehör zu Verkaufen',
    welcomeToMarketplace: 'Willkommen auf dem Mini Marktplatz!',
    buyAndSellMini: 'Kaufen und verkaufen Sie Mini-Autos, Teile, Zubehör und mehr. Erstellen Sie eine Anzeige mit Fotos und Preis.',
    noListingsYet: 'Noch keine Anzeigen. Seien Sie der Erste!',
    createFirstListing: 'Erste Anzeige erstellen',
    memberSince: 'Mitglied seit',
    yourTopics: 'Ihre Themen',
    noTopicsCreated: 'Sie haben noch keine Themen erstellt',
    likes: 'Gefällt mir',
    replies: 'Antworten',
    views: 'Aufrufe',
    writeComment: 'Kommentar schreiben...',
    postComment: 'Posten',
    rateSeller: 'Verkäufer bewerten',
    sendMessage: 'Nachricht senden',
    markAsSold: 'Als Verkauft markieren',
    markAvailable: 'Als Verfügbar markieren',
    sold: 'VERKAUFT',
    deleteTopic: 'Thema löschen',
    editTopic: 'Bearbeiten',
    location: 'Standort',
    selectCategory: 'Kategorie auswählen',
    
    // Forum Categories
    bonnetStripes: 'Motorhaubenstreifen',
    generalDiscussion: 'Allgemeine Diskussion',
    roofGraphics: 'Dachgrafiken',
    showcaseYourMini: 'Zeige Deinen Mini',
    sideStripes: 'Seitenstreifen',
    tipsAndTricks: 'Tipps und Tricks',
    
    // Create Listing/Topic Modal
    createNewListing: 'Neue Anzeige erstellen',
    createNewTopic: 'Neues Thema erstellen',
    postingAs: 'Posten als',
    sellingTips: 'Verkaufstipps:',
    sellingTipsText: 'Fügen Sie klare Fotos und den Artikelzustand hinzu. Legen Sie unten Ihren Preis und Standort fest.',
    category: 'Kategorie',
    newTopicCategory: 'Neue Kategorie',
    enterNewCategoryName: 'Neuen Kategorienamen eingeben',
    add: 'Hinzufügen',
    cancelUseExisting: 'Abbrechen - bestehende Kategorie verwenden',
    title: 'Titel',
    titlePlaceholder: 'Worum geht es in Ihrem Thema?',
    content: 'Inhalt',
    contentPlaceholder: 'Teilen Sie Ihre Gedanken, Tipps oder Fragen...',
    priceLabel: 'Preis (£)',
    pricePlaceholder: 'Preis eingeben (z.B. 99.99)',
    priceHelp: 'Leer lassen für "Preis auf Anfrage" oder "Kostenlos"',
    locationLabel: 'Standort',
    locationPlaceholder: 'z.B. Birmingham, London, Manchester',
    locationHelp: 'Helfen Sie Käufern zu wissen, wo sich der Artikel befindet',
    photosOptional: 'Fotos (optional)',
    uploading: 'Hochladen...',
    uploadPhoto: 'Foto hochladen',
    postTopic: 'Thema veröffentlichen',
    postListing: 'Anzeige veröffentlichen',
    priceOnRequest: 'Preis auf Anfrage',
    
    // My Profile Section
    topics: 'Themen',
    createYourFirstTopic: 'Erstellen Sie Ihr erstes Thema',
    noTopicsCreatedYet: 'Sie haben noch keine Themen erstellt',
    editProfile: 'Profil bearbeiten'
  },
  es: {
    // Navigation
    home: 'Inicio',
    shop: 'Tienda',
    search: 'Buscar',
    signIn: 'Iniciar Sesión',
    signOut: 'Cerrar Sesión',
    myAccount: 'Mi Cuenta',
    admin: 'Admin',
    adminPanel: 'Panel Admin',
    logout: 'Cerrar Sesión',
    
    // Cart & Checkout
    cart: 'Carrito',
    addToCart: 'Añadir al Carrito',
    checkout: 'Finalizar Compra',
    viewCart: 'Ver Carrito',
    continueShopping: 'Seguir Comprando',
    emptyCart: 'Tu carrito está vacío',
    clearCart: 'Vaciar Carrito',
    removeItem: 'Eliminar',
    updateQuantity: 'Actualizar Cantidad',
    yourCart: 'Tu Carrito',
    secureCheckout: 'Pago Seguro',
    placeOrder: 'Realizar Pedido',
    processing: 'Procesando...',
    
    // Products
    products: 'Productos',
    categories: 'Categorías',
    price: 'Precio',
    quantity: 'Cantidad',
    total: 'Total',
    subtotal: 'Subtotal',
    shipping: 'Envío',
    vat: 'IVA',
    description: 'Descripción',
    specifications: 'Especificaciones',
    reviews: 'Reseñas',
    sortBy: 'Ordenar por',
    searchProducts: 'Buscar productos',
    awaitingImage: 'Imagen pendiente',
    default: 'Por defecto',
    priceHighToLow: 'Precio: Mayor a Menor',
    priceLowToHigh: 'Precio: Menor a Mayor',
    nameAZ: 'Nombre: A-Z',
    nameZA: 'Nombre: Z-A',
    newestFirst: 'Más Reciente',
    filters: 'Filtros',
    noImage: 'Sin imagen',
    
    // Shop Categories
    shopByModel: 'Comprar por Modelo',
    paintProtection: 'Protección de Pintura',
    vinyl: 'Vinilo',
    vinylTools: 'Vinilo y Herramientas',
    miniRelated: 'Mini Relacionado',
    
    // Wishlist
    wishlist: 'Lista de Deseos',
    yourWishlist: 'Tu Lista de Deseos',
    emptyWishlist: 'Tu Lista de Deseos está Vacía',
    saveItemsHint: 'Guarda artículos haciendo clic en el icono del corazón.',
    clearAll: 'Borrar Todo',
    removedFromWishlist: 'eliminado de la lista de deseos',
    addedToWishlist: 'añadido a la lista de deseos',
    
    // Account
    account: 'Cuenta',
    my: 'Mi',
    orderHistory: 'Historial de Pedidos',
    noOrdersYet: 'Sin pedidos todavía',
    startShopping: 'Empezar a Comprar',
    homePage: 'Página de Inicio',
    clickPhotoToChange: 'Haz clic en la foto para cambiar',
    profileImageUpdated: '¡Imagen de perfil actualizada!',
    profileImageRemoved: 'Imagen de perfil eliminada',
    imageTooLarge: 'Imagen demasiado grande. Tamaño máximo: 5MB.',
    removeProfileImage: 'Eliminar imagen de perfil',
    
    // Auth
    login: 'Iniciar Sesión',
    register: 'Registrarse',
    createAccount: 'Crear Cuenta',
    welcomeBack: 'Bienvenido de nuevo',
    getStarted: 'Comenzar',
    fullName: 'Nombre Completo',
    password: 'Contraseña',
    confirmPassword: 'Confirmar Contraseña',
    loggingIn: 'Iniciando sesión...',
    creatingAccount: 'Creando cuenta...',
    dontHaveAccount: '¿No tienes cuenta?',
    createOne: 'Crear una',
    alreadyHaveAccount: '¿Ya tienes cuenta?',
    passwordsDontMatch: 'Las contraseñas no coinciden',
    passwordMinLength: 'La contraseña debe tener al menos 6 caracteres',
    accountCreatedSuccess: '¡Cuenta creada con éxito!',
    
    // Contact
    contactUs: 'Contáctanos',
    getInTouch: 'Ponte en Contacto',
    sendUsMessage: 'Envíanos un Mensaje',
    name: 'Nombre',
    subject: 'Asunto',
    message: 'Mensaje',
    sendMessage: 'Enviar Mensaje',
    sending: 'Enviando...',
    emailUs: 'Envíanos un Email',
    whatsApp: 'WhatsApp',
    textMessagesOnly: 'Solo mensajes de texto',
    businessHours: 'Horario de Atención',
    followUs: 'Síguenos',
    commonQuestions: 'Preguntas Frecuentes',
    weTypicallyRespond: 'Normalmente respondemos en 24-48 horas',
    
    // Forms
    email: 'Correo',
    phone: 'Teléfono',
    firstName: 'Nombre',
    lastName: 'Apellido',
    address: 'Dirección',
    city: 'Ciudad',
    postcode: 'Código Postal',
    country: 'País',
    optional: 'opcional',
    required: 'obligatorio',
    
    // Discount & Shipping
    discountCode: 'Código de Descuento',
    enterCouponCode: 'Introduce código de cupón',
    apply: 'Aplicar',
    estimateShipping: 'Calcular Envío',
    estimate: 'Calcular',
    orderSummary: 'Resumen del Pedido',
    addInstructionsToOrder: 'Añadir instrucciones al pedido',
    specialInstructionsPlaceholder: 'Introduce instrucciones especiales para tu pedido...',
    termsAcceptRequired: 'Por favor acepta los términos y condiciones para continuar',
    iAgreeTo: 'Acepto los',
    termsOfService: 'Términos de Servicio',
    and: 'y',
    shippingReturnsPolicy: 'Política de Envío y Devoluciones',
    sslSecure: 'SSL Seguro',
    sslSecureCheckout: 'Pago SSL Seguro',
    paymentsSecuredByStripe: 'Pagos seguros por Stripe',
    vatExemptNotice: 'Exento de IVA para pedidos internacionales. Pueden aplicarse tasas de importación.',
    
    // Shipping & Returns Page
    shippingReturns: 'Envío y Devoluciones',
    deliveryInformation: 'Información de Entrega',
    importantMadeToOrder: 'Importante: Hecho a Medida',
    madeToOrderNotice: '¡Todos nuestros gráficos se hacen a medida! Esto puede causar retrasos.',
    currentDispatchTime: 'Nuestro tiempo de envío actual es de 5 días laborables.',
    subjectToChange: 'Sujeto a cambios sin previo aviso.',
    pleaseBareinMind: 'Por favor ten esto en cuenta antes de pedir.',
    shippingCalculation: 'Cálculo de Envío',
    upsExtendedAreas: 'Áreas de Entrega Extendidas UPS',
    returnsPolicy: 'Política de Devoluciones',
    returnsAddress: 'Dirección de Devolución',
    questionsTitle: '¿Preguntas?',
    usa: 'EE.UU.',
    uk: 'Reino Unido',
    europe: 'Europa',
    worldwide: 'Todo el Mundo',
    backToCart: 'Volver al carrito',
    
    // How to Apply Page
    howToApply: 'Cómo Aplicar',
    watchTutorials: 'Mira nuestros tutoriales en video paso a paso',
    comingSoon: '¡Próximamente!',
    workingOnTutorials: 'Estamos preparando tutoriales en video. ¡Vuelve pronto!',
    allTutorials: 'Todos los Tutoriales',
    applicationTips: 'Consejos de Aplicación',
    cleanSurface: 'Superficie Limpia',
    cleanSurfaceTip: 'Asegúrate de que la superficie esté limpia, seca y libre de polvo.',
    temperature: 'Temperatura',
    temperatureTip: 'Aplica entre 15-25°C para mejores resultados.',
    takeYourTime: 'Tómate tu Tiempo',
    takeYourTimeTip: 'Trabaja lentamente para evitar burbujas. Usa una espátula.',
    
    // Support pages
    customsDuty: 'Aduanas / Aranceles',
    usaDelivery: 'EE.UU. y Tasas de Entrega',
    termsConditions: 'Términos y Condiciones',
    
    // Footer
    myOrders: 'Mis Pedidos',
    addresses: 'Direcciones',
    support: 'Soporte',
    allRightsReserved: 'Todos los derechos reservados.',
    paymentSecuredByStripe: 'Pago asegurado por Stripe',
    
    // Common
    loading: 'Cargando...',
    noResults: 'No se encontraron resultados',
    backToHome: 'Volver al Inicio',
    backToProducts: 'Volver a Productos',
    share: 'Compartir',
    colour: 'Color',
    instructions: 'Instrucciones',
    order: 'Pedido',
    status: 'Estado',
    date: 'Fecha',
    comingSoonLabel: 'Próximamente',
    creditDebitCard: 'Tarjeta de Crédito / Débito',
    poweredByStripe: 'Powered by Stripe',
    payPal: 'PayPal',
    paymentMethod: 'Método de Pago',
    shippingAddress: 'Dirección de Envío',
    contact: 'Contacto',
    
    // Notifications
    notifications: 'Notificaciones',
    noNotifications: 'Sin notificaciones',
    
    // Sell tab
    sell: 'Vender',
    sellOnForum: 'Vender en el Foro',
    miniStripesOwnersForum: 'Foro Mini Stripes Owners',
    
    // Forum / Owners Club
    backToAccount: 'Volver a la Cuenta',
    backToShop: 'Volver a la Tienda',
    shareYourMini: 'Comparte tu Mini, discute gráficos, vende y conéctate con otros entusiastas de Mini',
    myProfile: 'Mi Perfil',
    forum: 'Foro',
    gallery: 'Galería',
    forSale: 'En Venta',
    newTopic: 'Nuevo Tema',
    newListing: 'Nuevo Anuncio',
    searchTopics: 'Buscar temas...',
    filterByTopic: 'Filtrar por Tema',
    allTopics: 'Todos los Temas',
    noTopicsYet: '¡Aún no hay temas. Inicia la conversación!',
    createFirstTopic: 'Crear Primer Tema',
    photoGallery: 'Galería de Fotos',
    noPhotosYet: '¡Aún no hay fotos. Comparte tu Mini en el Foro!',
    goToForum: 'Ir al Foro',
    miniRelatedForSale: 'Mini Relacionado en Venta',
    welcomeToMarketplace: '¡Bienvenido al Mercado Mini!',
    buyAndSellMini: 'Compra y vende coches Mini, piezas, accesorios y más. Crea un anuncio con fotos y precio.',
    noListingsYet: '¡Aún no hay anuncios. Sé el primero en vender!',
    createFirstListing: 'Crear Primer Anuncio',
    memberSince: 'Miembro desde',
    yourTopics: 'Tus Temas',
    noTopicsCreated: 'Aún no has creado ningún tema',
    likes: 'me gusta',
    replies: 'respuestas',
    views: 'vistas',
    writeComment: 'Escribe un comentario...',
    postComment: 'Publicar',
    rateSeller: 'Calificar Vendedor',
    sendMessage: 'Enviar Mensaje',
    markAsSold: 'Marcar como Vendido',
    markAvailable: 'Marcar Disponible',
    sold: 'VENDIDO',
    deleteTopic: 'Eliminar Tema',
    editTopic: 'Editar',
    location: 'Ubicación',
    selectCategory: 'Seleccionar categoría',
    
    // Forum Categories
    bonnetStripes: 'Bandas del Capó',
    generalDiscussion: 'Discusión General',
    roofGraphics: 'Gráficos del Techo',
    showcaseYourMini: 'Muestra Tu Mini',
    sideStripes: 'Bandas Laterales',
    tipsAndTricks: 'Trucos y Consejos',
    
    // Create Listing/Topic Modal
    createNewListing: 'Crear Nuevo Anuncio',
    createNewTopic: 'Crear Nuevo Tema',
    postingAs: 'Publicando como',
    sellingTips: 'Consejos de venta:',
    sellingTipsText: 'Incluye fotos claras y el estado del artículo. Establece tu precio y ubicación abajo.',
    category: 'Categoría',
    newTopicCategory: 'Nueva Categoría',
    enterNewCategoryName: 'Ingresa el nombre de la nueva categoría',
    add: 'Agregar',
    cancelUseExisting: 'Cancelar - usar categoría existente',
    title: 'Título',
    titlePlaceholder: '¿De qué trata tu tema?',
    content: 'Contenido',
    contentPlaceholder: 'Comparte tus pensamientos, consejos o preguntas...',
    priceLabel: 'Precio (£)',
    pricePlaceholder: 'Ingresa el precio (ej. 99.99)',
    priceHelp: 'Deja vacío para "Precio a consultar" o "Gratis"',
    locationLabel: 'Ubicación',
    locationPlaceholder: 'ej. Birmingham, Londres, Manchester',
    locationHelp: 'Ayuda a los compradores a saber dónde está el artículo',
    photosOptional: 'Fotos (opcional)',
    uploading: 'Subiendo...',
    uploadPhoto: 'Subir Foto',
    postTopic: 'Publicar Tema',
    postListing: 'Publicar Anuncio',
    priceOnRequest: 'Precio a consultar',
    
    // My Profile Section
    topics: 'Temas',
    createYourFirstTopic: 'Crea Tu Primer Tema',
    noTopicsCreatedYet: 'Aún no has creado ningún tema',
    editProfile: 'Editar Perfil'
  },
  it: {
    // Navigation
    home: 'Home',
    shop: 'Negozio',
    search: 'Cerca',
    signIn: 'Accedi',
    signOut: 'Esci',
    myAccount: 'Il Mio Account',
    admin: 'Admin',
    adminPanel: 'Pannello Admin',
    logout: 'Esci',
    
    // Cart & Checkout
    cart: 'Carrello',
    addToCart: 'Aggiungi al Carrello',
    checkout: 'Checkout',
    viewCart: 'Vedi Carrello',
    continueShopping: 'Continua gli Acquisti',
    emptyCart: 'Il tuo carrello è vuoto',
    clearCart: 'Svuota Carrello',
    removeItem: 'Rimuovi',
    updateQuantity: 'Aggiorna Quantità',
    yourCart: 'Il Tuo Carrello',
    secureCheckout: 'Pagamento Sicuro',
    placeOrder: 'Effettua Ordine',
    processing: 'Elaborazione...',
    
    // Products
    products: 'Prodotti',
    categories: 'Categorie',
    price: 'Prezzo',
    quantity: 'Quantità',
    total: 'Totale',
    subtotal: 'Subtotale',
    shipping: 'Spedizione',
    vat: 'IVA',
    description: 'Descrizione',
    specifications: 'Specifiche',
    reviews: 'Recensioni',
    sortBy: 'Ordina per',
    searchProducts: 'Cerca prodotti',
    awaitingImage: 'Immagine in attesa',
    default: 'Predefinito',
    priceHighToLow: 'Prezzo: Alto a Basso',
    priceLowToHigh: 'Prezzo: Basso ad Alto',
    nameAZ: 'Nome: A-Z',
    nameZA: 'Nome: Z-A',
    newestFirst: 'Più Recenti',
    filters: 'Filtri',
    noImage: 'Nessuna immagine',
    
    // Shop Categories
    shopByModel: 'Acquista per Modello',
    paintProtection: 'Protezione Vernice',
    vinyl: 'Vinile',
    vinylTools: 'Vinile e Strumenti',
    miniRelated: 'Mini Correlati',
    
    // Wishlist
    wishlist: 'Lista dei Desideri',
    yourWishlist: 'La Tua Lista dei Desideri',
    emptyWishlist: 'La Tua Lista dei Desideri è Vuota',
    saveItemsHint: 'Salva gli articoli cliccando sull\'icona del cuore.',
    clearAll: 'Cancella Tutto',
    removedFromWishlist: 'rimosso dalla lista dei desideri',
    addedToWishlist: 'aggiunto alla lista dei desideri',
    
    // Account
    account: 'Account',
    my: 'Il Mio',
    orderHistory: 'Cronologia Ordini',
    noOrdersYet: 'Nessun ordine ancora',
    startShopping: 'Inizia a Fare Acquisti',
    homePage: 'Pagina Principale',
    clickPhotoToChange: 'Clicca sulla foto per cambiare',
    profileImageUpdated: 'Immagine profilo aggiornata!',
    profileImageRemoved: 'Immagine profilo rimossa',
    imageTooLarge: 'Immagine troppo grande. Dimensione massima: 5MB.',
    removeProfileImage: 'Rimuovi immagine profilo',
    
    // Auth
    login: 'Accedi',
    register: 'Registrati',
    createAccount: 'Crea Account',
    welcomeBack: 'Bentornato',
    getStarted: 'Inizia',
    fullName: 'Nome Completo',
    password: 'Password',
    confirmPassword: 'Conferma Password',
    loggingIn: 'Accesso in corso...',
    creatingAccount: 'Creazione account...',
    dontHaveAccount: 'Non hai un account?',
    createOne: 'Creane uno',
    alreadyHaveAccount: 'Hai già un account?',
    passwordsDontMatch: 'Le password non corrispondono',
    passwordMinLength: 'La password deve avere almeno 6 caratteri',
    accountCreatedSuccess: 'Account creato con successo!',
    
    // Contact
    contactUs: 'Contattaci',
    getInTouch: 'Mettiti in Contatto',
    sendUsMessage: 'Inviaci un Messaggio',
    name: 'Nome',
    subject: 'Oggetto',
    message: 'Messaggio',
    sendMessage: 'Invia Messaggio',
    sending: 'Invio...',
    emailUs: 'Inviaci una Email',
    whatsApp: 'WhatsApp',
    textMessagesOnly: 'Solo messaggi di testo',
    businessHours: 'Orari di Apertura',
    followUs: 'Seguici',
    commonQuestions: 'Domande Frequenti',
    weTypicallyRespond: 'Rispondiamo solitamente entro 24-48 ore',
    
    // Forms
    email: 'Email',
    phone: 'Telefono',
    firstName: 'Nome',
    lastName: 'Cognome',
    address: 'Indirizzo',
    city: 'Città',
    postcode: 'CAP',
    country: 'Paese',
    optional: 'opzionale',
    required: 'obbligatorio',
    
    // Discount & Shipping
    discountCode: 'Codice Sconto',
    enterCouponCode: 'Inserisci codice coupon',
    apply: 'Applica',
    estimateShipping: 'Calcola Spedizione',
    estimate: 'Calcola',
    orderSummary: 'Riepilogo Ordine',
    addInstructionsToOrder: 'Aggiungi istruzioni all\'ordine',
    specialInstructionsPlaceholder: 'Inserisci istruzioni speciali per il tuo ordine...',
    termsAcceptRequired: 'Accetta i termini e condizioni per continuare',
    iAgreeTo: 'Accetto i',
    termsOfService: 'Termini di Servizio',
    and: 'e',
    shippingReturnsPolicy: 'Politica di Spedizione e Resi',
    sslSecure: 'SSL Sicuro',
    sslSecureCheckout: 'Pagamento SSL Sicuro',
    paymentsSecuredByStripe: 'Pagamenti sicuri con Stripe',
    vatExemptNotice: 'Esente IVA per ordini internazionali. Potrebbero applicarsi dazi doganali.',
    
    // Shipping & Returns Page
    shippingReturns: 'Spedizione e Resi',
    deliveryInformation: 'Informazioni sulla Consegna',
    importantMadeToOrder: 'Importante: Fatto su Ordinazione',
    madeToOrderNotice: 'Tutti i nostri grafici sono fatti su ordinazione! Potrebbero esserci ritardi.',
    currentDispatchTime: 'Il nostro tempo di spedizione attuale è di 5 giorni lavorativi.',
    subjectToChange: 'Soggetto a modifiche senza preavviso.',
    pleaseBareinMind: 'Per favore tieni presente questo prima di ordinare.',
    shippingCalculation: 'Calcolo Spedizione',
    upsExtendedAreas: 'Aree di Consegna Estese UPS',
    returnsPolicy: 'Politica di Reso',
    returnsAddress: 'Indirizzo per i Resi',
    questionsTitle: 'Domande?',
    usa: 'USA',
    uk: 'Regno Unito',
    europe: 'Europa',
    worldwide: 'Tutto il Mondo',
    backToCart: 'Torna al carrello',
    
    // How to Apply Page
    howToApply: 'Come Applicare',
    watchTutorials: 'Guarda i nostri video tutorial passo dopo passo',
    comingSoon: 'Prossimamente!',
    workingOnTutorials: 'Stiamo preparando video tutorial utili. Torna presto!',
    allTutorials: 'Tutti i Tutorial',
    applicationTips: 'Consigli per l\'Applicazione',
    cleanSurface: 'Superficie Pulita',
    cleanSurfaceTip: 'Assicurati che la superficie sia pulita, asciutta e priva di polvere.',
    temperature: 'Temperatura',
    temperatureTip: 'Applica tra 15-25°C per i migliori risultati.',
    takeYourTime: 'Prenditi il Tuo Tempo',
    takeYourTimeTip: 'Lavora lentamente per evitare bolle. Usa una spatola.',
    
    // Support pages
    customsDuty: 'Dogana / Dazi',
    usaDelivery: 'USA e Dazi di Consegna',
    termsConditions: 'Termini e Condizioni',
    
    // Footer
    myOrders: 'I Miei Ordini',
    addresses: 'Indirizzi',
    support: 'Supporto',
    allRightsReserved: 'Tutti i diritti riservati.',
    paymentSecuredByStripe: 'Pagamento protetto da Stripe',
    
    // Common
    loading: 'Caricamento...',
    noResults: 'Nessun risultato trovato',
    backToHome: 'Torna alla Home',
    backToProducts: 'Torna ai Prodotti',
    share: 'Condividi',
    colour: 'Colore',
    instructions: 'Istruzioni',
    order: 'Ordine',
    status: 'Stato',
    date: 'Data',
    comingSoonLabel: 'Prossimamente',
    creditDebitCard: 'Carta di Credito / Debito',
    poweredByStripe: 'Powered by Stripe',
    payPal: 'PayPal',
    paymentMethod: 'Metodo di Pagamento',
    shippingAddress: 'Indirizzo di Spedizione',
    contact: 'Contatto',
    
    // Notifications
    notifications: 'Notifiche',
    noNotifications: 'Nessuna notifica',
    
    // Sell tab
    sell: 'Vendi',
    sellOnForum: 'Vendi sul Forum',
    miniStripesOwnersForum: 'Forum Mini Stripes Owners',
    
    // Forum / Owners Club
    backToAccount: 'Torna all\'Account',
    backToShop: 'Torna al Negozio',
    shareYourMini: 'Condividi la tua Mini, discuti di grafiche, vendi e connettiti con altri appassionati di Mini',
    myProfile: 'Il Mio Profilo',
    forum: 'Forum',
    gallery: 'Galleria',
    forSale: 'In Vendita',
    newTopic: 'Nuovo Argomento',
    newListing: 'Nuovo Annuncio',
    searchTopics: 'Cerca argomenti...',
    filterByTopic: 'Filtra per Argomento',
    allTopics: 'Tutti gli Argomenti',
    noTopicsYet: 'Ancora nessun argomento. Inizia la conversazione!',
    createFirstTopic: 'Crea il Primo Argomento',
    photoGallery: 'Galleria Foto',
    noPhotosYet: 'Ancora nessuna foto. Condividi la tua Mini nel Forum!',
    goToForum: 'Vai al Forum',
    miniRelatedForSale: 'Mini Correlato in Vendita',
    welcomeToMarketplace: 'Benvenuto nel Mercato Mini!',
    buyAndSellMini: 'Compra e vendi auto Mini, ricambi, accessori e altro. Crea un annuncio con foto e prezzo.',
    noListingsYet: 'Ancora nessun annuncio. Sii il primo a vendere!',
    createFirstListing: 'Crea il Primo Annuncio',
    memberSince: 'Membro dal',
    yourTopics: 'I Tuoi Argomenti',
    noTopicsCreated: 'Non hai ancora creato argomenti',
    likes: 'mi piace',
    replies: 'risposte',
    views: 'visualizzazioni',
    writeComment: 'Scrivi un commento...',
    postComment: 'Pubblica',
    rateSeller: 'Valuta Venditore',
    sendMessage: 'Invia Messaggio',
    markAsSold: 'Segna come Venduto',
    markAvailable: 'Segna Disponibile',
    sold: 'VENDUTO',
    deleteTopic: 'Elimina Argomento',
    editTopic: 'Modifica',
    location: 'Posizione',
    selectCategory: 'Seleziona categoria',
    
    // Forum Categories
    bonnetStripes: 'Strisce del Cofano',
    generalDiscussion: 'Discussione Generale',
    roofGraphics: 'Grafiche del Tetto',
    showcaseYourMini: 'Mostra la Tua Mini',
    sideStripes: 'Strisce Laterali',
    tipsAndTricks: 'Trucchi e Suggerimenti',
    
    // Create Listing/Topic Modal
    createNewListing: 'Crea Nuovo Annuncio',
    createNewTopic: 'Crea Nuovo Argomento',
    postingAs: 'Pubblicando come',
    sellingTips: 'Consigli di vendita:',
    sellingTipsText: 'Includi foto chiare e le condizioni dell\'articolo. Imposta il prezzo e la posizione qui sotto.',
    category: 'Categoria',
    newTopicCategory: 'Nuova Categoria',
    enterNewCategoryName: 'Inserisci il nome della nuova categoria',
    add: 'Aggiungi',
    cancelUseExisting: 'Annulla - usa categoria esistente',
    title: 'Titolo',
    titlePlaceholder: 'Di cosa tratta il tuo argomento?',
    content: 'Contenuto',
    contentPlaceholder: 'Condividi i tuoi pensieri, consigli o domande...',
    priceLabel: 'Prezzo (£)',
    pricePlaceholder: 'Inserisci il prezzo (es. 99.99)',
    priceHelp: 'Lascia vuoto per "Prezzo su richiesta" o "Gratis"',
    locationLabel: 'Posizione',
    locationPlaceholder: 'es. Birmingham, Londra, Manchester',
    locationHelp: 'Aiuta gli acquirenti a sapere dove si trova l\'articolo',
    photosOptional: 'Foto (opzionale)',
    uploading: 'Caricamento...',
    uploadPhoto: 'Carica Foto',
    postTopic: 'Pubblica Argomento',
    postListing: 'Pubblica Annuncio',
    priceOnRequest: 'Prezzo su richiesta',
    
    // My Profile Section
    topics: 'Argomenti',
    createYourFirstTopic: 'Crea il Tuo Primo Argomento',
    noTopicsCreatedYet: 'Non hai ancora creato argomenti',
    editProfile: 'Modifica Profilo'
  },
  ja: {
    // Navigation
    home: 'ホーム',
    shop: 'ショップ',
    search: '検索',
    signIn: 'ログイン',
    signOut: 'ログアウト',
    myAccount: 'マイアカウント',
    admin: '管理者',
    adminPanel: '管理パネル',
    logout: 'ログアウト',
    
    // Cart & Checkout
    cart: 'カート',
    addToCart: 'カートに追加',
    checkout: '購入手続き',
    viewCart: 'カートを見る',
    continueShopping: '買い物を続ける',
    emptyCart: 'カートは空です',
    clearCart: 'カートを空にする',
    removeItem: '削除',
    updateQuantity: '数量を更新',
    yourCart: 'カート',
    secureCheckout: '安全な決済',
    placeOrder: '注文する',
    processing: '処理中...',
    
    // Products
    products: '製品',
    categories: 'カテゴリー',
    price: '価格',
    quantity: '数量',
    total: '合計',
    subtotal: '小計',
    shipping: '配送',
    vat: '消費税',
    description: '説明',
    specifications: '仕様',
    reviews: 'レビュー',
    sortBy: '並び替え',
    searchProducts: '製品を検索',
    awaitingImage: '画像準備中',
    default: 'デフォルト',
    priceHighToLow: '価格: 高い順',
    priceLowToHigh: '価格: 安い順',
    nameAZ: '名前: A-Z',
    nameZA: '名前: Z-A',
    newestFirst: '新着順',
    filters: 'フィルター',
    noImage: '画像なし',
    
    // Shop Categories
    shopByModel: 'モデル別に購入',
    paintProtection: '塗装保護',
    vinyl: 'ビニール',
    vinylTools: 'ビニール＆ツール',
    miniRelated: 'Mini関連',
    
    // Wishlist
    wishlist: 'ウィッシュリスト',
    yourWishlist: 'ウィッシュリスト',
    emptyWishlist: 'ウィッシュリストは空です',
    saveItemsHint: 'ハートアイコンをクリックして商品を保存してください。',
    clearAll: 'すべて削除',
    removedFromWishlist: 'ウィッシュリストから削除されました',
    addedToWishlist: 'ウィッシュリストに追加されました',
    
    // Account
    account: 'アカウント',
    my: 'マイ',
    orderHistory: '注文履歴',
    noOrdersYet: '注文履歴はありません',
    startShopping: 'ショッピングを始める',
    homePage: 'ホームページ',
    clickPhotoToChange: 'クリックして写真を変更',
    profileImageUpdated: 'プロフィール画像を更新しました！',
    profileImageRemoved: 'プロフィール画像を削除しました',
    imageTooLarge: '画像が大きすぎます。最大サイズ: 5MB。',
    removeProfileImage: 'プロフィール画像を削除',
    
    // Auth
    login: 'ログイン',
    register: '登録',
    createAccount: 'アカウント作成',
    welcomeBack: 'おかえりなさい',
    getStarted: '始める',
    fullName: '氏名',
    password: 'パスワード',
    confirmPassword: 'パスワード確認',
    loggingIn: 'ログイン中...',
    creatingAccount: 'アカウント作成中...',
    dontHaveAccount: 'アカウントをお持ちでない方',
    createOne: '作成する',
    alreadyHaveAccount: 'すでにアカウントをお持ちの方',
    passwordsDontMatch: 'パスワードが一致しません',
    passwordMinLength: 'パスワードは6文字以上必要です',
    accountCreatedSuccess: 'アカウントが作成されました！',
    
    // Contact
    contactUs: 'お問い合わせ',
    getInTouch: 'お問い合わせ',
    sendUsMessage: 'メッセージを送る',
    name: '名前',
    subject: '件名',
    message: 'メッセージ',
    sendMessage: '送信',
    sending: '送信中...',
    emailUs: 'メールを送る',
    whatsApp: 'WhatsApp',
    textMessagesOnly: 'テキストメッセージのみ',
    businessHours: '営業時間',
    followUs: 'フォローする',
    commonQuestions: 'よくある質問',
    weTypicallyRespond: '通常24-48時間以内にご返信いたします',
    
    // Forms
    email: 'メール',
    phone: '電話',
    firstName: '名',
    lastName: '姓',
    address: '住所',
    city: '市区町村',
    postcode: '郵便番号',
    country: '国',
    optional: '任意',
    required: '必須',
    
    // Discount & Shipping
    discountCode: '割引コード',
    enterCouponCode: 'クーポンコードを入力',
    apply: '適用',
    estimateShipping: '配送料を計算',
    estimate: '計算',
    orderSummary: '注文概要',
    addInstructionsToOrder: '注文に指示を追加',
    specialInstructionsPlaceholder: '特別な指示を入力してください...',
    termsAcceptRequired: '続行するには利用規約に同意してください',
    iAgreeTo: '同意します',
    termsOfService: '利用規約',
    and: 'と',
    shippingReturnsPolicy: '配送・返品ポリシー',
    sslSecure: 'SSL保護',
    sslSecureCheckout: 'SSL安全決済',
    paymentsSecuredByStripe: 'Stripeによる安全な支払い',
    vatExemptNotice: '国際注文は消費税免除です。輸入関税がかかる場合があります。',
    
    // Shipping & Returns Page
    shippingReturns: '配送と返品',
    deliveryInformation: '配送情報',
    importantMadeToOrder: '重要: 受注生産',
    madeToOrderNotice: '当社の製品はすべて受注生産です！遅延が生じる場合があります。',
    currentDispatchTime: '現在の発送予定日数は5営業日です。',
    subjectToChange: '予告なく変更される場合があります。',
    pleaseBareinMind: 'ご注文前にご了承ください。',
    shippingCalculation: '送料計算',
    upsExtendedAreas: 'UPS配送拡大エリア',
    returnsPolicy: '返品ポリシー',
    returnsAddress: '返品先住所',
    questionsTitle: 'ご質問は？',
    usa: 'アメリカ',
    uk: 'イギリス',
    europe: 'ヨーロッパ',
    worldwide: '世界各国',
    backToCart: 'カートに戻る',
    
    // How to Apply Page
    howToApply: '貼り付け方',
    watchTutorials: 'ステップバイステップのビデオチュートリアルをご覧ください',
    comingSoon: '近日公開！',
    workingOnTutorials: 'ビデオチュートリアルを準備中です。もうしばらくお待ちください！',
    allTutorials: 'すべてのチュートリアル',
    applicationTips: '貼り付けのコツ',
    cleanSurface: '清潔な表面',
    cleanSurfaceTip: '表面が清潔で乾燥しており、ほこりがないことを確認してください。',
    temperature: '温度',
    temperatureTip: '最良の結果を得るために15-25°Cで貼り付けてください。',
    takeYourTime: 'ゆっくりと',
    takeYourTimeTip: '気泡を避けるためにゆっくり作業してください。スキージを使用してください。',
    
    // Support pages
    customsDuty: '関税/税金',
    usaDelivery: 'アメリカと配送料',
    termsConditions: '利用規約',
    
    // Footer
    myOrders: '注文履歴',
    addresses: '住所',
    support: 'サポート',
    allRightsReserved: '全著作権所有。',
    paymentSecuredByStripe: 'Stripeによる安全な支払い',
    
    // Common
    loading: '読み込み中...',
    noResults: '結果が見つかりません',
    backToHome: 'ホームに戻る',
    backToProducts: '製品に戻る',
    share: '共有',
    colour: '色',
    instructions: '指示',
    order: '注文',
    status: 'ステータス',
    date: '日付',
    comingSoonLabel: '近日公開',
    creditDebitCard: 'クレジット/デビットカード',
    poweredByStripe: 'Powered by Stripe',
    payPal: 'PayPal',
    paymentMethod: '支払い方法',
    shippingAddress: '配送先住所',
    contact: '連絡先',
    
    // Notifications
    notifications: '通知',
    noNotifications: '通知はありません',
    
    // Sell tab
    sell: '販売',
    sellOnForum: 'フォーラムで販売',
    miniStripesOwnersForum: 'Mini Stripes Owners フォーラム',
    
    // Forum / Owners Club
    backToAccount: 'アカウントに戻る',
    backToShop: 'ショップに戻る',
    shareYourMini: 'あなたのミニを共有し、グラフィックについて話し合い、販売し、ミニ愛好家と繋がりましょう',
    myProfile: 'マイプロフィール',
    forum: 'フォーラム',
    gallery: 'ギャラリー',
    forSale: '販売中',
    newTopic: '新しいトピック',
    newListing: '新しい出品',
    searchTopics: 'トピックを検索...',
    filterByTopic: 'トピックで絞り込む',
    allTopics: 'すべてのトピック',
    noTopicsYet: 'まだトピックがありません。会話を始めましょう！',
    createFirstTopic: '最初のトピックを作成',
    photoGallery: 'フォトギャラリー',
    noPhotosYet: 'まだ写真がありません。フォーラムであなたのミニを共有してください！',
    goToForum: 'フォーラムへ',
    miniRelatedForSale: 'ミニ関連販売中',
    welcomeToMarketplace: 'ミニマーケットプレイスへようこそ！',
    buyAndSellMini: 'ミニの車、パーツ、アクセサリーなどを売買できます。「新しい出品」をクリックして写真と価格を設定してください。',
    noListingsYet: 'まだ出品がありません。最初の出品者になりましょう！',
    createFirstListing: '最初の出品を作成',
    memberSince: 'メンバー登録日',
    yourTopics: 'あなたのトピック',
    noTopicsCreated: 'まだトピックを作成していません',
    likes: 'いいね',
    replies: '返信',
    views: '閲覧',
    writeComment: 'コメントを書く...',
    postComment: '投稿',
    rateSeller: '出品者を評価',
    sendMessage: 'メッセージを送る',
    markAsSold: '売却済みにする',
    markAvailable: '販売中にする',
    sold: '売却済',
    deleteTopic: 'トピックを削除',
    editTopic: '編集',
    location: '場所',
    selectCategory: 'カテゴリを選択',
    
    // Forum Categories
    bonnetStripes: 'ボンネットストライプ',
    generalDiscussion: '一般討論',
    roofGraphics: 'ルーフグラフィック',
    showcaseYourMini: 'あなたのミニを見せて',
    sideStripes: 'サイドストライプ',
    tipsAndTricks: 'ヒントとコツ',
    
    // Create Listing/Topic Modal
    createNewListing: '新しい出品を作成',
    createNewTopic: '新しいトピックを作成',
    postingAs: '投稿者',
    sellingTips: '販売のヒント:',
    sellingTipsText: '明確な写真と商品の状態を含めてください。以下で価格と場所を設定してください。',
    category: 'カテゴリ',
    newTopicCategory: '新しいカテゴリ',
    enterNewCategoryName: '新しいカテゴリ名を入力',
    add: '追加',
    cancelUseExisting: 'キャンセル - 既存のカテゴリを使用',
    title: 'タイトル',
    titlePlaceholder: 'トピックの内容は何ですか？',
    content: '内容',
    contentPlaceholder: 'あなたの考え、ヒント、質問を共有してください...',
    priceLabel: '価格 (£)',
    pricePlaceholder: '価格を入力 (例: 99.99)',
    priceHelp: '「価格応相談」または「無料」の場合は空欄のままにしてください',
    locationLabel: '場所',
    locationPlaceholder: '例: バーミンガム、ロンドン、マンチェスター',
    locationHelp: '購入者が商品の場所を知るのに役立ちます',
    photosOptional: '写真（任意）',
    uploading: 'アップロード中...',
    uploadPhoto: '写真をアップロード',
    postTopic: 'トピックを投稿',
    postListing: '出品を投稿',
    priceOnRequest: '価格応相談',
    
    // My Profile Section
    topics: 'トピック',
    createYourFirstTopic: '最初のトピックを作成',
    noTopicsCreatedYet: 'まだトピックを作成していません',
    editProfile: 'プロフィールを編集'
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check localStorage first
    try {
      const saved = localStorage.getItem('preferredLanguage');
      if (saved && SUPPORTED_LANGUAGES[saved]) {
        return saved;
      }
    } catch (e) {}
    // Auto-detect from browser
    const browserLang = navigator.language.split('-')[0];
    return SUPPORTED_LANGUAGES[browserLang] ? browserLang : 'en';
  });
  
  const [translationCache, setTranslationCache] = useState({});

  // Save language preference when it changes
  useEffect(() => {
    try {
      localStorage.setItem('preferredLanguage', language);
    } catch (e) {}
  }, [language]);

  // Get static UI translation
  const t = useCallback((key) => {
    const translations = UI_TRANSLATIONS[language] || UI_TRANSLATIONS.en;
    return translations[key] || UI_TRANSLATIONS.en[key] || key;
  }, [language]);

  // Translate dynamic text via API
  const translateText = useCallback(async (text) => {
    if (!text || language === 'en') return text;
    
    const cacheKey = `${text}:${language}`;
    if (translationCache[cacheKey]) {
      return translationCache[cacheKey];
    }
    
    try {
      const response = await axios.post(`${API}/api/translate/text`, {
        text,
        target_lang: language,
        source_lang: 'en'
      });
      
      const translated = response.data.translated || text;
      setTranslationCache(prev => ({ ...prev, [cacheKey]: translated }));
      return translated;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    }
  }, [language, translationCache]);

  // Translate batch of texts
  const translateBatch = useCallback(async (texts) => {
    if (language === 'en') return texts;
    
    try {
      const response = await axios.post(`${API}/api/translate/batch`, {
        texts,
        target_lang: language
      });
      
      return response.data.translations || texts;
    } catch (error) {
      console.error('Batch translation error:', error);
      return texts;
    }
  }, [language]);

  const value = {
    language,
    setLanguage,
    languages: SUPPORTED_LANGUAGES,
    t,
    translateText,
    translateBatch
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
